/**
 * A question group's rubric, set the way the real screen sets it.
 *
 * On the CD IELTS screen the task line is roman and the answer-sheet
 * instruction beneath it is italic — "Write the correct letter, A-F, in boxes
 * on your answer sheet" — with the letter range and any word limit in bold.
 * That typography is not decoration: it is how a candidate tells the task
 * apart from the clerical instruction at a glance, so it is reproduced here
 * rather than printing the whole rubric as one grey paragraph.
 */

/** Lines the paper italicises: the clerical instruction, not the task. */
const ITALIC =
  /^\s*(?:NB\b|N\.B\.|Write\b|Match each\b|In boxes\b|You may use\b|Choose the correct letter\b|Choose\b(?=.*\b(?:from the box|write the correct)\b))/i;

/** "NO MORE THAN TWO WORDS", "ONE WORD ONLY" — always bold on the real screen. */
const LIMIT =
  /\b(?:NO MORE THAN [A-Z]+ WORDS?(?: AND\/OR A NUMBER)?|[A-Z]+ WORDS? ONLY|ONE WORD (?:ONLY|AND\/OR A NUMBER)|TWO WORDS AND\/OR A NUMBER)\b/g;

/** "A-F", "i-viii" — the range of letters an answer may take. */
const ROMAN = '(?:i|ii|iii|iv|v|vi|vii|viii|ix|x|xi|xii)';
const RANGE = new RegExp(`\\b(?:[A-K]\\s*[\\u2013-]\\s*[A-K]|${ROMAN}\\s*[\\u2013-]\\s*${ROMAN})\\b`, 'g');

/**
 * "A, B, C or D" — the choices of a multiple-choice question, with or without
 * the comma before "or". Papers write it both ways, and the version with the
 * comma was left unbolded.
 */
const CHOICES = /\b[A-K](?:,\s*[A-K])*,?\s+or\s+[A-K]\b/g;

export const decorateRubric = (line: string): string => decorate(line);

/**
 * "Choose TWO letters" — the count is bold on the real screen, and it is the
 * one word in the rubric a candidate cannot afford to miss.
 */
const COUNT = /\b(?:ONE|TWO|THREE|FOUR|FIVE)\b/;

/**
 * All of the above in one pass.
 *
 * Applied one after another, an earlier match would be re-matched inside its
 * own <strong> — "NO MORE THAN TWO WORDS" would end up with TWO wrapped again
 * inside it. Longest and most specific first, so the limit wins over the bare
 * count.
 */
const EMPHASIS = new RegExp(
  [LIMIT.source, CHOICES.source, RANGE.source, COUNT.source].join('|'),
  'g',
);

const escape = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const strong = (s: string) => `<strong>${s}</strong>`;

function decorate(line: string): string {
  return escape(line)
    .replace(EMPHASIS, strong)
    .replace(/^(NB|N\.B\.)\b/, strong);
}

/**
 * True when the rubric already states the word limit in the paper's own words,
 * so the screen does not print a second, synthesised copy of it.
 *
 * Tested with its own non-global copy: a `g` regex carries `lastIndex` between
 * calls, which would make every second group answer differently.
 */
const LIMIT_ONCE = new RegExp(LIMIT.source);
export const statesWordLimit = (instructions: string | null): boolean =>
  Boolean(instructions && LIMIT_ONCE.test(instructions));

export function Rubric({ text }: { text: string }) {
  return (
    <>
      {text
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean)
        .map((line, i) => (
          <p
            key={i}
            className={`xrubric${ITALIC.test(line) ? ' xrubric--em' : ''}`}
            /* Instructions are read as closely as the passage — a candidate
               marks "NO MORE THAN TWO WORDS" so they stop losing marks to it. */
            data-markable
            dangerouslySetInnerHTML={{ __html: decorate(line) }}
          />
        ))}
    </>
  );
}
